import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { formatInUserTimezone, getTimezoneAbbreviation, getEffectiveTimezone } from '@/utils/timezoneUtils';
import CustomLeagueFormContent from './CustomLeagueFormContent';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface LeagueFormData {
  name: string;
  division: string;
  description: string;
  website: string;
  logo: string;
  autoSyncEnabled: boolean;
  standingsSource: 'calculated' | 'imported';
  playoffsEnabled: boolean;
  playoffSpots: number;
  winPoints: number;
  drawPoints: number;
  lossPoints: number;
  syncTimeOfDay: string;
  syncFrequency: string;
  syncPriority: number;
}

export interface EditingLeague {
  id: string;
  name: string;
  division?: string;
  description?: string;
  website?: string;
  logoUrl?: string;
  autoSyncEnabled?: boolean;
  standingsSource?: string;
  playoffsEnabled?: boolean;
  playoffSpots?: number;
  winPoints?: number;
  drawPoints?: number;
  lossPoints?: number;
  syncTimeOfDay?: string;
  syncFrequency?: string;
  syncPriority?: number;
  nextSyncAt?: string;
  isGlobalLeague?: boolean;
  // GlobalSchedule settings (single source of truth for catalog leagues)
  globalSchedule?: {
    autoSyncEnabled: boolean;
    syncFrequency: string;
    syncTimeOfDay: string;
    syncPriority: number;
    winPoints: number;
    drawPoints: number;
    lossPoints: number;
    nextSyncAt?: string;
  };
}

interface LeagueFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: LeagueFormData, discoveryPlatform: DiscoveryPlatform) => void;
  onDiscoveryRequest: (formData: LeagueFormData, platform: DiscoveryPlatform) => void;
  editingLeague: EditingLeague | null;
  submitting: boolean;
  user: any;
  orgSettings: any;
  syncTimePreview: { nextSyncAt: string; displayTime: string; timezone: string } | null;
  initialFormData?: LeagueFormData | null; // Used to restore form data when navigating back from discovery
  onBack?: () => void; // Optional callback to go back to catalog dialog
  fromCatalog?: boolean; // True when adding from catalog (shows "Add League" instead of "Add Custom League")
  readOnly?: boolean; // True when viewing catalog league settings (non-super admin)
  pendingAssignment?: { playerName: string; teamName: string }; // Show assignment summary
}

// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_FORM_DATA: LeagueFormData = {
  name: '',
  division: '',
  description: '',
  website: '',
  logo: '',
  autoSyncEnabled: false,
  standingsSource: 'imported',
  playoffsEnabled: false,
  playoffSpots: 4,
  winPoints: 3,
  drawPoints: 1,
  lossPoints: 0,
  syncTimeOfDay: '06:00:00',
  syncFrequency: 'daily',
  syncPriority: 1,
};

const detectGotSportEventUrl = (url: string): boolean => {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    const isGotSport = urlObj.hostname.includes('gotsport.com') &&
                       urlObj.pathname.includes('/org_event/events/');
    if (!isGotSport) return false;

    // Schedule URLs don't need discovery - they already point to a specific schedule
    // Schedule URLs have /schedules in the path or group= parameter
    const isScheduleUrl = urlObj.pathname.includes('/schedules') ||
                          urlObj.searchParams.has('group');
    return !isScheduleUrl;
  } catch {
    return false;
  }
};

const detectSincSportsLeagueUrl = (url: string): boolean => {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    if (!urlObj.hostname.includes('sincsports.com')) return false;

    const divParam = urlObj.searchParams.get('div');
    // If div=N or no div param, it's the main league page (needs discovery)
    return !divParam || divParam === 'N' || divParam === 'n';
  } catch {
    return false;
  }
};

export type DiscoveryPlatform = 'gotsport' | 'sincsports' | null;

// eslint-disable-next-line react-refresh/only-export-components
export const detectDiscoveryPlatform = (url: string): DiscoveryPlatform => {
  if (detectGotSportEventUrl(url)) return 'gotsport';
  if (detectSincSportsLeagueUrl(url)) return 'sincsports';
  return null;
};

const LeagueFormDialog: React.FC<LeagueFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  onDiscoveryRequest,
  editingLeague,
  submitting,
  user,
  orgSettings,
  syncTimePreview: _syncTimePreview, // eslint-disable-line @typescript-eslint/no-unused-vars
  initialFormData,
  onBack,
  fromCatalog,
  readOnly = false,
  pendingAssignment
}) => {
  // Form state - local to this component for performance
  const [formData, setFormData] = useState<LeagueFormData>(DEFAULT_FORM_DATA);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [syncTime, setSyncTime] = useState<Dayjs | null>(dayjs().hour(6).minute(0));
  const [discoveryPlatform, setDiscoveryPlatform] = useState<DiscoveryPlatform>(null);

  // League details are read-only when adding from catalog (values come from catalog)
  const detailsReadOnly = readOnly || (!!fromCatalog && !editingLeague);

  // Reset form when dialog opens/closes or editingLeague changes
  useEffect(() => {
    if (open) {
      setFieldErrors({});

      if (editingLeague) {
        // For catalog leagues, use GlobalSchedule settings (single source of truth)
        const gs = editingLeague.globalSchedule;
        const isCatalogLeague = !!gs;

        const nextSyncAtSource = isCatalogLeague ? gs.nextSyncAt : editingLeague.nextSyncAt;
        const extractedTime = nextSyncAtSource
          ? extractTimeFromNextSync(nextSyncAtSource, user, orgSettings)
          : null;

        setFormData({
          name: editingLeague.name || '',
          division: editingLeague.division || '',
          description: editingLeague.description || '',
          website: editingLeague.website || '',
          logo: editingLeague.logoUrl || '',
          autoSyncEnabled: isCatalogLeague ? (gs.autoSyncEnabled ?? false) : (editingLeague.autoSyncEnabled ?? false),
          standingsSource: (editingLeague.standingsSource as 'calculated' | 'imported') || 'imported',
          playoffsEnabled: editingLeague.playoffsEnabled ?? false,
          playoffSpots: editingLeague.playoffSpots ?? 4,
          winPoints: isCatalogLeague ? (gs.winPoints ?? 3) : (editingLeague.winPoints ?? 3),
          drawPoints: isCatalogLeague ? (gs.drawPoints ?? 1) : (editingLeague.drawPoints ?? 1),
          lossPoints: isCatalogLeague ? (gs.lossPoints ?? 0) : (editingLeague.lossPoints ?? 0),
          syncTimeOfDay: isCatalogLeague ? (gs.syncTimeOfDay || '06:00:00') : (extractedTime || editingLeague.syncTimeOfDay || '06:00:00'),
          syncFrequency: isCatalogLeague ? (gs.syncFrequency || 'daily') : (editingLeague.syncFrequency || 'daily'),
          syncPriority: isCatalogLeague ? (gs.syncPriority ?? 1) : (editingLeague.syncPriority ?? 1),
        });

        // Set sync time picker
        const timeForPicker = isCatalogLeague
          ? (gs.syncTimeOfDay || '06:00:00')
          : (extractedTime || editingLeague.syncTimeOfDay || '06:00:00');
        const [hours, minutes] = timeForPicker.split(':').map(Number);
        setSyncTime(dayjs().hour(hours).minute(minutes));

        setDiscoveryPlatform(detectDiscoveryPlatform(editingLeague.website || ''));
      } else if (initialFormData) {
        // Restore form data when navigating back from discovery
        setFormData(initialFormData);
        const [hours, minutes] = initialFormData.syncTimeOfDay.split(':').map(Number);
        setSyncTime(dayjs().hour(hours).minute(minutes));
        setDiscoveryPlatform(detectDiscoveryPlatform(initialFormData.website || ''));
      } else {
        setFormData(DEFAULT_FORM_DATA);
        setSyncTime(dayjs().hour(6).minute(0));
        setDiscoveryPlatform(null);
      }
    }
  }, [open, editingLeague, initialFormData, user, orgSettings]);

  // Sync the time picker state with form data
  useEffect(() => {
    if (syncTime) {
      const timeString = syncTime.format('HH:mm:ss');
      setFormData(prev => ({ ...prev, syncTimeOfDay: timeString }));
    }
  }, [syncTime]);

  const handleInputChange = (field: keyof LeagueFormData, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // Auto-enable sync when website is entered
      if (field === 'website' && value && value.trim() !== '' && !prev.autoSyncEnabled) {
        updated.autoSyncEnabled = true;
      }

      // Auto-disable sync when website is removed
      if (field === 'website' && (!value || value.trim() === '') && prev.autoSyncEnabled) {
        updated.autoSyncEnabled = false;
      }

      return updated;
    });

    // Check if this URL needs discovery
    if (field === 'website') {
      setDiscoveryPlatform(detectDiscoveryPlatform(value || ''));
    }

    // Clear field error when user types
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSyncTimeChange = (newTime: Dayjs | null) => {
    setSyncTime(newTime);
  };

  const getSyncSettingsSummary = (): string => {
    if (!formData.autoSyncEnabled) {
      return '(Disabled)';
    }

    // For new leagues, sync time is auto-assigned by the backend
    if (!editingLeague) {
      return '(Daily, auto-assigned)';
    }

    const timeStr = editingLeague?.nextSyncAt
      ? formatInUserTimezone(
          editingLeague.nextSyncAt,
          'h:mm A',
          false,
          user,
          orgSettings
        )
      : syncTime
        ? syncTime.format('h:mm A')
        : '12:00 AM';

    const tzAbbr = getTimezoneAbbreviation(user, orgSettings);
    const frequency = formData.syncFrequency === 'daily' ? 'Daily' :
                     formData.syncFrequency === 'weekly' ? 'Weekly' :
                     formData.syncFrequency === 'every_2_days' ? 'Every 2 days' :
                     formData.syncFrequency;

    return `(${frequency} at ${timeStr} ${tzAbbr})`;
  };

  const handleSubmitClick = () => {
    // Validate required fields
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'League name is required';
    }

    // Require website for new leagues
    if (!editingLeague && (!formData.website || formData.website.trim() === '')) {
      errors.website = 'Website is required for new leagues';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (discoveryPlatform && !editingLeague && !fromCatalog) {
      // Wizard mode: open discovery dialog (only for custom leagues, not catalog imports)
      onDiscoveryRequest(formData, discoveryPlatform);
    } else {
      // Normal submit (including catalog imports where division is already selected)
      onSubmit(formData, discoveryPlatform);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth transitionDuration={0} disableAutoFocus>
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {readOnly ? <VisibilityIcon /> : editingLeague ? <EditIcon /> : <AddIcon />}
          {readOnly ? 'View League Settings' : editingLeague ? 'Edit League' : fromCatalog ? 'Add League' : 'Add Custom League'}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 450, maxHeight: 450, overflow: 'auto', p: 0 }}>
        <CustomLeagueFormContent
          formData={formData}
          fieldErrors={fieldErrors}
          onInputChange={handleInputChange}
          detailsReadOnly={detailsReadOnly}
          readOnly={readOnly}
          editingLeague={editingLeague}
          fromCatalog={!!fromCatalog}
          discoveryPlatform={discoveryPlatform}
          syncTime={syncTime}
          onSyncTimeChange={handleSyncTimeChange}
          user={user}
          orgSettings={orgSettings}
          pendingAssignment={pendingAssignment}
          getSyncSettingsSummary={getSyncSettingsSummary}
        />
      </DialogContent>
      <DialogActions sx={{ borderTop: 1, borderColor: 'divider' }}>
        {onBack && !editingLeague && !readOnly && (
          <Button variant="outlined" onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ mr: 'auto' }}>
            Back
          </Button>
        )}
        <Button variant="outlined" onClick={onClose} startIcon={<CancelIcon />}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button
            onClick={handleSubmitClick}
            variant="contained"
            disabled={submitting || (!!discoveryPlatform && !editingLeague && !formData.website.trim())}
            sx={{ minWidth: 80 }}
            startIcon={submitting ? undefined : (
              editingLeague ? <SaveIcon /> : (discoveryPlatform && !fromCatalog ? <ArrowForwardIcon /> : <AddIcon />)
            )}
          >
            {submitting ? <CircularProgress size={20} /> : (
              editingLeague ? 'Update' : (discoveryPlatform && !fromCatalog ? 'Next' : 'Add')
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Helper function to extract time from nextSyncAt in user's timezone
const extractTimeFromNextSync = (
  nextSyncAt: string | null,
  user: any,
  orgSettings: any
): string | null => {
  if (!nextSyncAt) return null;

  try {
    const timezone = getEffectiveTimezone(user, orgSettings);
    const utcTime = dayjs.utc(nextSyncAt);
    const localTime = utcTime.tz(timezone);

    return localTime.format('HH:mm:ss');
  } catch (error) {
    console.error('Error extracting sync time:', error);
    return null;
  }
};

export default LeagueFormDialog;
